import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { RewardsHubServicesApi } from "../api/RewardsHubServicesApi";
import { EventLogger } from "../utils/EventLogger";
import { GLOBAL_CONST, REWARD_OFFERING } from "../../constants";
import { sendAnalytics, delay } from "../../utils";
interface CompetitionState {
  hasEntered: boolean;
  loading: boolean;
  initialLoading: boolean;
  dialogOpen: boolean;
  entrySuccess: boolean;
}

type ValidOfferIds = REWARD_OFFERING;

export const useCompetition = (
  offerId: ValidOfferIds,
  additionalData?: Record<string, unknown>
) => {
  const [state, setState] = useState<CompetitionState>({
    hasEntered: false,
    loading: false,
    initialLoading: true,
    dialogOpen: false,
    entrySuccess: false,
  });

  const api = useMemo(() => new RewardsHubServicesApi(), []);
  const accountId = GLOBAL_CONST.DATA.accountId;

  const hasChecked = useRef(false);

  useEffect(() => {
    if (hasChecked.current) return;
    hasChecked.current = true;

    if (!accountId) {
      setState((prev) => ({ ...prev, initialLoading: false }));
      return;
    }

    (async () => {
      try {
        const hasEntered = await api.getCompetition({
          accountId,
          offerId,
        });
        setState((prev) => ({
          ...prev,
          hasEntered,
          dialogOpen: hasEntered,
          entrySuccess: hasEntered,
          initialLoading: false,
        }));
      } catch (error) {
        await EventLogger.logErrorEvent("Error checking competition entry status", error);
        setState((prev) => ({ ...prev, initialLoading: false }));
      }
    })();
  }, [api, accountId, offerId]);

  const enterCompetition = useCallback(async () => {
    setState((prev) => ({ ...prev, dialogOpen: true, loading: true }));
    try {
      await api.putCompetition({ accountId, offerId, data: additionalData });
      setState((prev) => ({
        ...prev,
        entrySuccess: true,
        hasEntered: true,
        loading: false,
      }));
      // iOS: Await analytics and add delay before any potential subsequent native interactions or state changes that might trigger them.
      await sendAnalytics("competitionEntrySuccess");
      await delay(100);
    } catch (error: any) {
      setState((prev) => ({
        ...prev,
        entrySuccess: false,
        loading: false,
      }));
      // iOS: Await analytics and add delay.
      const errorMsg = error?.response?.data?.message || error?.message || "Unknown error";
      const debugInfo = `putCompetition+${error?.response?.status || "unknown"}+${error?.response?.data?.errorCode || "unknown"}`;
      await sendAnalytics("competitionEntryFailure", { errorMsg, debugInfo });
      await delay(100);
      await EventLogger.logErrorEvent("Error entering competition", error);
    }
  }, [api, accountId, offerId, additionalData]);

  return {
    ...state,
    enterCompetition,
    closeDialog: () => setState((prev) => ({ ...prev, dialogOpen: false })),
  };
};